import React from "react";
import ReactDOM from "react-dom";
import downArrowSvg from "assets/img/arrow-down.svg";
import upArrowSvg from "assets/img/arrow-up.svg";
import emptyTableStateSvg from "assets/img/empty-table-state.svg";
import searchSvg from "assets/img/filter-data.svg"; // NEW: search icon
import cx from "classnames";
import Loading from "components/Loading";
import MobileTable from "components/MobileTable";
import { Body } from "components/Typography";
import ReactTable, {
  Column,
  ColumnRenderProps,
  FinalState,
  ReactTableDefaults,
  RowInfo,
  RowRenderProps,
  SortingRule,
} from "react-table";
import selectTable from "react-table/lib/hoc/selectTable";
import "react-table/react-table.css";
import NoDataAvailable from "./NoDataLightTheme";

const SelectTable = selectTable(ReactTable);

export interface ColumnProps {
  show?: boolean;
  field: string;
  header: string;
  hideSearchIcon?: boolean;
  className?: string;
  formatter?: (value: string, row?: any, index?: number) => string | JSX.Element | null;
  sortMethod?: (a: string | number, b: string | number) => number;
  dataQa?: string;
  sortable?: boolean;
  minWidth?: number;
  maxWidth?: number;
  wrap?: boolean;
  textAlign?: string;
  Expander?: any;
}

interface TableProps {
  data: object[];
  columns: ColumnProps[];
  emptyState?: string;
  headerClassName?: string;
  onRowClick?: (e: React.MouseEvent<any>) => void;
  isMobile?: boolean;
  isScrollable?: boolean;
  style?: object;
  loading?: boolean;
  showPagination?: boolean;
  pageSize?: number;
  showEmptyMobileHeader?: boolean;
  showHeader?: boolean;
  columnStyles?: React.CSSProperties;
  defaultSort?: boolean;
  customSort?: SortingRule[];
  useSelect?: boolean;
  selectInput?: any;
  updateSelections?: (selection: any) => void;
  LoadingComponent?: Element;
}

interface ColumnState {
  id: string;
  desc: boolean;
  sortable: boolean;
}

interface TableState {
  columns: ColumnState[];
  selection?: any;
  selectAll?: any;
  expandedRows?: any;

  // FILTER-related state
  filterOpenColumn?: string | null; // which column's filter dropdown is open
  columnFilters?: { [colId: string]: string[] }; // applied filters (selected values)
  tempSelections?: { [colId: string]: Set<string> }; // temp selections while dropdown open
  filterSearchText?: { [colId: string]: string }; // per-column search text inside dropdown
}

const defaultColStyle = {
  padding: 8,
  marginTop: "-1px",
  borderTop: "1px solid #ebebeb",
  borderLeft: "none",
  borderRight: "none",
  fontSize: 14,
  fontFamily: "Poppins",
  fontWeight: 800,
  color: "black",
  whiteSpace: "unset",
};

const DEFAULT_PAGE_SIZE = 100;

export default class Table extends React.Component<TableProps, TableState> {
  // store computed fixed position for the portal dropdown
  dropdownPosition?: { top: number; left: number } | null = null;

  constructor(props: TableProps) {
    super(props);
    this.state = {
      columns: props.columns.map((c) => ({
        id: c.field,
        desc: true,
        sortable: c.sortable !== false,
      })),
      filterOpenColumn: null,
      columnFilters: {},
      tempSelections: {},
      filterSearchText: {},
    };
    this.toggleSelection = this.toggleSelection.bind(this);
    this.toggleAll = this.toggleAll.bind(this);
    this.toggleFilterDropdown = this.toggleFilterDropdown.bind(this);
    this.applyFilterForColumn = this.applyFilterForColumn.bind(this);
    this.clearFilterForColumn = this.clearFilterForColumn.bind(this);
    this.toggleTempSelection = this.toggleTempSelection.bind(this);
    this.onTempSelectAllToggle = this.onTempSelectAllToggle.bind(this);
    this.onFilterSearchChange = this.onFilterSearchChange.bind(this);

    this.handleOutsideMouseDown = this.handleOutsideMouseDown.bind(this);
    this.handleWindowScrollOrResize = this.handleWindowScrollOrResize.bind(this);
  }

  public selectTable: any;

  // ---------- Selection methods (unchanged) ----------
  public toggleSelection = (key: any, shift: any, row: any) => {
    let selection: any = this.state.selection ? { ...this.state.selection } : {};
    const keyIndex = selection[key];
    if (keyIndex) {
      delete selection[key];
    } else {
      selection[key] = row;
    }
    if (this.state.selectAll) {
      this.setState({ selectAll: !this.state.selectAll });
    }
    this.setState({ selection });
    if (this.props.updateSelections) {
      this.props.updateSelections(selection);
    }
  };

  public toggleAll = () => {
    const selectAll = this.state.selectAll ? false : true;
    const selection: any = {};
    if (selectAll) {
      const wrappedInstance = this.selectTable.getWrappedInstance();
      const currentRecords = wrappedInstance.getResolvedState().sortedData;
      currentRecords.forEach((item: any) => {
        if (item._original) {
          selection[item._original._id] = item._original;
        }
      });
    }
    this.setState({ selectAll, selection });
    if (this.props.updateSelections) {
      this.props.updateSelections(selection);
    }
  };

  public isSelected = (key: any) => {
    return this.state.selection ? this.state.selection[key] : false;
  };

  selectInput = (props: any) => {
    return (
      <input
        type={props.selectType || "checkbox"}
        className={"dir-checkbox"}
        checked={props.checked ? true : false}
        id={`checkbox-${props.id}`}
        onClick={(e) => {
          const { shiftKey } = e;
          e.stopPropagation();
          props.onClick(props.id, shiftKey, props.row);
        }}
        onChange={() => {}}
        value=""
      />
    );
  };

  selectAllInput = (props: any) => {
    return (
      <input
        type={props.selectType || "checkbox"}
        className={"dir-checkbox"}
        checked={this.state.selectAll}
        id={`select-all-checkbox`}
        onClick={(e) => {
          const { shiftKey } = e;
          e.stopPropagation();
          props.onClick(props.id, shiftKey, props.row);
        }}
        onChange={() => {}}
        value=""
      />
    );
  };

  // ---------- FILTER helper methods ----------

  // Add listeners for outside clicks + scroll/resize while dropdown open
  addDropdownListeners() {
    document.addEventListener("mousedown", this.handleOutsideMouseDown);
    // window.addEventListener("scroll", this.handleWindowScrollOrResize, true);
    window.removeEventListener("scroll", this.handleWindowScrollOrResize, true);
    window.addEventListener("resize", this.handleWindowScrollOrResize);
  }
  removeDropdownListeners() {
    document.removeEventListener("mousedown", this.handleOutsideMouseDown);
    window.removeEventListener("scroll", this.handleWindowScrollOrResize, true);
    window.removeEventListener("resize", this.handleWindowScrollOrResize);
  }

  handleOutsideMouseDown(e: MouseEvent) {
    // If click outside portal dropdown, close it.
    // dropdown root has data-qa attribute filter-dropdown-<column>
    if (!this.state.filterOpenColumn) return;
    const dd = document.querySelector(`[data-qa="filter-dropdown-${this.state.filterOpenColumn}"]`);
    const icon = document.querySelector(`[data-qa="search-icon-${this.state.filterOpenColumn}"]`);
    if (dd && dd.contains(e.target as Node)) return;
    if (icon && icon.contains(e.target as Node)) return;
    // otherwise close
    this.setState({ filterOpenColumn: null }, () => {
      this.dropdownPosition = null;
      this.removeDropdownListeners();
    });
  }

  handleWindowScrollOrResize() {
    // On scroll or resize we close the dropdown to avoid misalignment.
    if (!this.state.filterOpenColumn) return;
    this.setState({ filterOpenColumn: null }, () => {
      this.dropdownPosition = null;
      this.removeDropdownListeners();
    });
  }

  // Toggle open/close of filter dropdown for a column
  toggleFilterDropdown(columnId?: string | null) {
    // if called with undefined or null, close
    if (!columnId) {
      this.setState({ filterOpenColumn: null }, () => {
        this.dropdownPosition = null;
        this.removeDropdownListeners();
      });
      return;
    }
    const currentlyOpen = this.state.filterOpenColumn;
    if (currentlyOpen === columnId) {
      // close
      this.setState({ filterOpenColumn: null }, () => {
        this.dropdownPosition = null;
        this.removeDropdownListeners();
      });
    } else {
      // compute position anchored to search icon element
      // find the DOM element we set on the header span: data-qa="search-icon-<columnId>"
      const el = document.querySelector(`[data-qa="search-icon-${columnId}"]`);
      if (el) {
        const rect = el.getBoundingClientRect();
        // position dropdown so its left aligns to icon's left
        const dropdownWidth = 260;
        const left = Math.max(8, rect.right - dropdownWidth); // ensure not off-screen
        const top = rect.bottom + 6;
        this.dropdownPosition = { top, left };
      } else {
        // fallback center-top of viewport
        this.dropdownPosition = { top: 80, left: 20 };
      }

      // initialize tempSelections from existing applied filter
      const applied = this.state.columnFilters && this.state.columnFilters[columnId];
      const tempSet = new Set<string>((applied && applied.map(String)) || []);
      this.setState(
        (prev) => ({
          filterOpenColumn: columnId,
          tempSelections: {
            ...(prev.tempSelections || {}),
            [columnId]: tempSet,
          },
          filterSearchText: {
            ...(prev.filterSearchText || {}),
            [columnId]: "",
          },
        }),
        () => {
          // add outside click & scroll listeners
          this.addDropdownListeners();
        }
      );
    }
  }

  // get unique values for a column from props.data (stringified)
  getUniqueValuesForColumn(columnId: string) {
    const { data } = this.props;
    const set = new Set<string>();
    data.forEach((row: any) => {
      const val = row && row[columnId];
      // treat null/undefined as empty string and skip
      if (val !== null && val !== undefined) {
        set.add(String(val));
      }
    });
    // return sorted array
    return Array.from(set).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  }

  // toggle an option in tempSelections for the open column
  toggleTempSelection(columnId: string, value: string) {
    this.setState((prev) => {
      const prevTemp = (prev.tempSelections && prev.tempSelections[columnId]) || new Set<string>();
      const newSet = new Set(prevTemp);
      if (newSet.has(value)) newSet.delete(value);
      else newSet.add(value);
      return {
        tempSelections: {
          ...(prev.tempSelections || {}),
          [columnId]: newSet,
        },
      };
    });
  }

  // Select/deselect all (temp) for column
  onTempSelectAllToggle(columnId: string, allValues: string[]) {
    this.setState((prev) => {
      const prevTemp = (prev.tempSelections && prev.tempSelections[columnId]) || new Set<string>();
      let newSet = new Set<string>(prevTemp);
      if (newSet.size === allValues.length) {
        // all selected -> clear
        newSet = new Set<string>();
      } else {
        // select all
        newSet = new Set<string>(allValues);
      }
      return {
        tempSelections: {
          ...(prev.tempSelections || {}),
          [columnId]: newSet,
        },
      };
    });
  }

  onFilterSearchChange(columnId: string, txt: string) {
    this.setState((prev) => ({
      filterSearchText: {
        ...(prev.filterSearchText || {}),
        [columnId]: txt,
      },
    }));
  }

  // Apply the tempSelections as the applied column filter
  applyFilterForColumn(columnId: string) {
    this.setState(
      (prev) => {
        const set = (prev.tempSelections && prev.tempSelections[columnId]) || new Set<string>();
        const arr = Array.from(set);
        const newColumnFilters = {
          ...(prev.columnFilters || {}),
          ...(arr.length ? { [columnId]: arr } : {}),
        };
        // if arr empty -> delete key
        if (!arr.length) {
          delete newColumnFilters[columnId];
        }
        return {
          columnFilters: newColumnFilters,
          filterOpenColumn: null, // close dropdown
        };
      },
      () => {
        // close and cleanup listeners
        this.dropdownPosition = null;
        this.removeDropdownListeners();
      }
    );
  }

  // Clear applied filter for a column
  clearFilterForColumn(columnId: string) {
    this.setState(
      (prev) => {
        const newColumnFilters = { ...(prev.columnFilters || {}) };
        if (newColumnFilters[columnId]) delete newColumnFilters[columnId];
        // also reset temp selection
        const newTemp = { ...(prev.tempSelections || {}) };
        if (newTemp[columnId]) delete newTemp[columnId];
        const newFilterSearchText = { ...(prev.filterSearchText || {}) };
        if (newFilterSearchText[columnId]) delete newFilterSearchText[columnId];
        return {
          columnFilters: newColumnFilters,
          tempSelections: newTemp,
          filterSearchText: newFilterSearchText,
          filterOpenColumn: null,
        };
      },
      () => {
        // cleanup
        this.dropdownPosition = null;
        this.removeDropdownListeners();
      }
    );
  }

  // Build the array expected by ReactTable's `filtered` prop
  getFilteredArray() {
    const { columnFilters } = this.state;
    if (!columnFilters) return [];
    return Object.keys(columnFilters).map((id) => {
      // we'll stringify array as value; our defaultFilterMethod handles it
      return { id, value: JSON.stringify(columnFilters[id]) };
    });
  }

  // defaultFilterMethod to handle our multi-select filters (JSON-stringified arrays)
  defaultFilterMethod = (filter: any, row: any, column: any) => {
    // If filter.value is empty treat as pass
    if (!filter || !filter.value) return true;

    // Attempt parse JSON array
    let allowed: string[] = [];
    try {
      allowed = JSON.parse(filter.value);
      if (!Array.isArray(allowed)) allowed = [String(filter.value)];
    } catch {
      // fallback: split by || as older fallback
      allowed = String(filter.value).split("||").map(String);
    }

    if (!allowed || allowed.length === 0) return true;

    const rowValue = row[filter.id];
    // if row value undefined/null treat as "" and only match if "" included
    const rv = rowValue === undefined || rowValue === null ? "" : String(rowValue);
    return allowed.includes(rv);
  };

  // ---------- Rendering helpers ----------

  // Render the filter dropdown for a column (portal) - anchored to document.body and positioned fixed
  renderFilterDropdown(columnId: string) {
    const allValues = this.getUniqueValuesForColumn(columnId);
    const tempSet = (this.state.tempSelections && this.state.tempSelections[columnId]) || new Set();
    const searchText = (this.state.filterSearchText && this.state.filterSearchText[columnId]) || "";

    const optionsToShow = allValues.filter((v) => v.toLowerCase().includes(searchText.toLowerCase()));

    const portalNode = document.body;

    // Use the user's provided dropdown styling exactly
    const containerStyle: React.CSSProperties = {
      position: "fixed",
      top: (this.dropdownPosition && this.dropdownPosition.top) || 80,
      left: (this.dropdownPosition && this.dropdownPosition.left) || 20,
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: 4,
      padding: 10,
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      zIndex: 99999,
      width: 260,
    };

    return ReactDOM.createPortal(
      <div
        style={containerStyle}
        data-qa={`filter-dropdown-${columnId}`}
        onMouseDown={(e) => {
          // Prevent document click handlers from closing dropdown
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => {
          e.stopPropagation();
        }}
      >
        {/* 🔍 Search input */}
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search..."
            value={searchText}
            autoFocus
            onChange={(e) =>
              this.onFilterSearchChange(columnId, e.target.value)
            }
            onMouseDown={(e) => {
              // IMPORTANT: do NOT preventDefault here
              e.stopPropagation();
            }}
            style={{
              width: "100%",
              padding: "6px 28px 6px 8px",
              fontSize: 12,
              border: "1px solid #ccc",
              borderRadius: 4,
            }}
          />

          {searchText !== "" && (
            <span
              onMouseDown={(e) => {
                // Safe to prevent default on non-input
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={() =>
                this.setState((prev) => ({
                  filterSearchText: {
                    ...(prev.filterSearchText || {}),
                    [columnId]: "",
                  },
                }))
              }
              style={{
                position: "absolute",
                right: 8,
                cursor: "pointer",
                fontSize: 14,
                color: "#555",
                userSelect: "none",
              }}
            >
          ×
        </span>
          )}
        </div>

        {/* 📜 Options list */}
        <div
          style={{
            marginTop: 8,
            maxHeight: 200,
            overflowY: "auto",
            borderTop: "1px solid #F3F4F6",
            paddingTop: 8,
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          {optionsToShow.length === 0 ? (
            <div style={{ padding: "6px 8px", fontSize: 12, color: "#6B7280" }}>
              No values
            </div>
          ) : (
            optionsToShow.map((val) => {
              const checked = tempSet.has(val);
              return (
                <label
                  key={val}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    style={{ accentColor: "#E5B42F" } as any}
                    onChange={() =>
                      this.toggleTempSelection(columnId, val)
                    }
                  />
                  <span
                    style={{
                      userSelect: "none",
                      fontFamily: "Poppins",
                      fontWeight: 700,
                    }}
                  >
                {val}
              </span>
                </label>
              );
            })
          )}
        </div>

        {/* 🔘 Action buttons */}
        <div
          style={{
            marginTop: 8,
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              this.setState((prev) => ({
                tempSelections: {
                  ...(prev.tempSelections || {}),
                  [columnId]: new Set<string>(),
                },
              }));
            }}
            style={{
              flex: 1,
              padding: "6px 16px",
              borderRadius: 4,
              border: "none",
              background: "#F3F4F6",
              color: "#000",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Cancel
          </button>

          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => this.applyFilterForColumn(columnId)}
            style={{
              flex: 1,
              padding: "6px 16px",
              borderRadius: 4,
              border: "none",
              background: "#E5B42F",
              color: "#000",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Apply
          </button>
        </div>
      </div>,
      portalNode
    );

  }

  // ---------- transformColumn (keeps your Cell behavior) ----------
  public transformColumn = (
    { show = true, ...column }: ColumnProps,
    styles: React.CSSProperties = {}
  ) => {
    // We'll set a Header that just outputs the text (existing h6),
    // the ThComponent (in the table render) will place the search icon and dropdown next to sort icons.
    return {
      Header: (props: ColumnRenderProps) => <h6 data-qa={column.dataQa}>{column.header}</h6>,
      accessor: column.field,
      id: column.field,
      show,
      style: column.textAlign ? { textAlign: column.textAlign, ...defaultColStyle, ...styles } : { ...defaultColStyle, ...styles },
      ...(column.minWidth ? { minWidth: column.minWidth } : {}),
      ...(column.sortMethod ? { sortMethod: column.sortMethod } : {}),
      maxWidth: column.maxWidth,
      className: cx({ "no-wrap": column.wrap }),
      expander: column.field === "expander",
      Expander: column.Expander,
      Cell: (row: RowRenderProps) => {
        const classes = `body b3 ${column.className}`;
        return (
          <span className={classes} data-qa={column.dataQa}>
            {column.formatter ? column.formatter(row.value, row.original, row.index) : row.value ? row.value : ""}
          </span>
        );
      },
    };
  };

  // ---------- RENDER ----------
  public render() {
    const {
      data,
      columns,
      emptyState,
      headerClassName,
      isScrollable,
      onRowClick,
      loading,
      showPagination = this.props.data.length > 500,
      pageSize = DEFAULT_PAGE_SIZE,
      isMobile,
      showEmptyMobileHeader = true,
      defaultSort = true,
      customSort,
      columnStyles = {},
    } = this.props;

    if (isMobile) {
      if (data.length === 0) {
        return this.renderMobileEmptyState();
      } else {
        return data.map((datum, i) => (
          <MobileTable
            onRowClick={onRowClick}
            index={i}
            key={i}
            columns={columns}
            data={datum}
            isScrollable={isScrollable}
            showEmptyHeader={showEmptyMobileHeader}
          />
        ));
      }
    }

    const { toggleSelection, toggleAll, isSelected } = this;
    const extraProps = {
      isSelected,
      toggleAll,
      toggleSelection,
      ref: (r: any) => {
        this.selectTable = r;
      },
      LoadingComponent: () => (
        <div className={`-loading ${loading && `-active`}`}>
          <div className="-loading-inner">
            <Loading />
          </div>
        </div>
      ),
    };

    // Helper ThComponent function to be used in both SelectTable and ReactTable render blocks
    const createThComponent = (tableType: "select" | "normal" = "normal") => {
      // tableType is unused currently but kept for future differences
      return ({ toggleSort, className, children, onClick, column, ...rest }: any) => {
        const { showHeader = true } = this.props;

        // find sortable state for this column from local state
        const filtered = this.state.columns.filter((c) => c.id === column);
        const [[desc, sortable]] =
          filtered.length > 0 ? filtered.map((c) => [c.desc, c.sortable]) : [[true, true]];

        if (!showHeader) return null;

        // Determine whether we should show search icon.
        const selectedColumn = columns.find((c) => c.field === column);
        const shouldShowSearchIcon =
          sortable !== false && column !== 'action_id' && !selectedColumn?.hideSearchIcon;
        const columnId = column; // column id string

        const isDropdownOpen = this.state.filterOpenColumn === columnId;

        const isFiltered = !!(this.state.columnFilters && this.state.columnFilters[columnId] && this.state.columnFilters[columnId].length);

        return (
          <div
            className={cx("rt-th", className)}
            onClick={(e) => {
              e.persist();
              return onClick(e, () => sortable && toggleSort && toggleSort(e));
            }}
            role="columnheader"
            tabIndex={-1}
            style={{ position: "relative", display: 'flex', alignItems: 'center', gap: 6 }} // to anchor dropdown (icon)
            {...rest}
          >
            {children}
            {sortable && (
              <span
                style={{
                  marginLeft: 10,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                {desc ? <img src={downArrowSvg} alt="" /> : <img src={upArrowSvg} alt="" />}
              </span>
            )}

            {shouldShowSearchIcon && (
              <span
                style={{
                  marginLeft: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  // stop header click (sorting) from firing
                  e.stopPropagation();
                  this.toggleFilterDropdown(columnId);
                }}
                data-qa={`search-icon-${columnId}`}
                title="Filter"
              >
                <img src={searchSvg} alt="filter" />
              </span>
            )}

            {/* Option A: show Clear text next to icons when filter is applied */}
            {isFiltered && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  this.clearFilterForColumn(columnId);
                }}
                style={{
                  backgroundColor: "#F3F4F6",
                  border: "1px solid #E5E7EB",
                  padding: "2px 6px",
                  borderRadius: 12,
                  fontSize: 11,
                  cursor: "pointer",
                }}
                data-qa={`clear-filter-${columnId}`}
                title="Clear Filter"
              >
                Clear
              </button>
            )}

            {isDropdownOpen && columnId && this.renderFilterDropdown(columnId)}
          </div>
        );
      };
    };

    // ---------- Render SelectTable (if useSelect) ----------
    if (this.props.useSelect) {
      const defaultExpandedRows = data.map((element, index) => {
        return { index: true };
      });
      return (
        <SelectTable
          {...extraProps}
          selectType="checkbox"
          loading={loading}
          SelectInputComponent={this.selectInput}
          SelectAllInputComponent={this.selectAllInput}
          loadingText=""
          data={data}
          columns={columns.map((c) => this.transformColumn(c, columnStyles))}
          showPagination={showPagination}
          pageSize={showPagination ? pageSize : data.length}
          showPageJump={false}
          showPageSizeOptions={false}
          resizable={false}
          minRows={0}
          expanded={defaultExpandedRows}
          defaultSorted={
            (customSort && customSort) ||
            (defaultSort && [{ id: "field", desc: true }]) ||
            undefined
          }
          style={{
            border: 0,
            ...this.props.style,
          }}
          filtered={this.getFilteredArray()} // APPLY FILTERS
          defaultFilterMethod={this.defaultFilterMethod}
          column={{
            ...ReactTableDefaults.column,
            headerClassName,
            headerStyle: {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: 8,
              border: "1px solid #ebebeb",
              borderLeft: "none",
              borderRight: "none",
              borderTop: "none",
              background: "transparent",
              boxShadow: "none",
              fontSize: 12,
              fontWeight: "bold",
              fontFamily: "serif",
              outline: "none",
            },
            style: { ...defaultColStyle, ...columnStyles },
          }}
          ThComponent={createThComponent("select")}
          getTheadProps={(state: FinalState, rowInfo: undefined) => {
            return {
              style: {
                boxShadow: "none",
              },
            };
          }}
          getTheadThProps={(
            state: FinalState,
            rowInfo: undefined,
            column: Column | undefined,
            instance: any
          ) => ({
            ...ReactTableDefaults.getTheadThProps,
            column: column && column.id,
            onClick: (e: React.SyntheticEvent<HTMLElement>, handleOriginal: () => any) => {
              this.setState(
                (prevState: TableState) => ({
                  columns: prevState.columns.map((c) => {
                    if (c.id === (column && column.id)) {
                      c.desc = !c.desc;
                    } else {
                      c.desc = true;
                    }
                    return c;
                  }),
                }),
                () => {
                  if (handleOriginal) {
                    handleOriginal();
                  }
                }
              );
            },
          })}
          noDataText={emptyState || "No data found"}
          getNoDataProps={() => ({
            style: {
              position: "relative",
              transform: "none",
              width: 150,
              height: 150,
              marginTop: 30,
            },
          })}
          NoDataComponent={({ children, style }: any) => {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "16em",
                }}
              >
                {loading ? (
                  <div
                    style={{
                      zIndex: 10000,
                      minHeight: "16em",
                    }}
                  >
                    <Loading />
                  </div>
                ) : (
                  <React.Fragment>
                    <img
                      style={style}
                      src={emptyTableStateSvg}
                      data-qa="empty-state-image"
                      alt=""
                    />
                    <Body
                      type="2"
                      style={{
                        marginTop: 12,
                        height:"10px"
                      }}
                      data-qa="empty-state-text"
                    >
                      {children}
                    </Body>
                  </React.Fragment>
                )}
              </div>
            );
          }}
          getTrProps={(
            state: any,
            rowInfo: RowInfo | undefined,
            column: undefined,
            instance: any
          ) => {
            let additionalClasses = "";

            if (rowInfo && rowInfo.row.version && rowInfo.row.version < 0) {
              additionalClasses = "previous-file";
            }

            return {
              style: {
                cursor: onRowClick ? "pointer" : "inherit",

                // ALTERNATE ROW COLORS
                background:
                  rowInfo && rowInfo?.index % 2 === 0
                    ? "#FDF19EC2"
                    : "#F9F9F9",
                whiteSpace: "normal", // allows content to wrap
              },

              onClick: (e: any) =>
                onRowClick && onRowClick(rowInfo && rowInfo.row._original),

              className: `${additionalClasses} ${onRowClick && "table-row-clickable"}`,
            };
          }}

          getTdProps={() => {
            return { style: { overflow: "visible" } };
          }}
          getTbodyProps={() => {
            return {
              style: this.props.isScrollable ? { overflowY: "scroll" } : { overflow: "visible" },
            };
          }}
          getTableProps={() => {
            return {
              style: this.props.isScrollable ? { overflowY: "scroll" } : { overflow: "visible" },
            };
          }}
        />
      );
    }

    // ---------- Render normal ReactTable ----------
    return (
      <ReactTable
        loading={loading}
        loadingText=""
        data={data}
        columns={columns.map((c) => this.transformColumn(c, columnStyles))}
        showPagination={showPagination}
        pageSize={showPagination ? pageSize : data.length}
        showPageJump={false}
        showPageSizeOptions={false}
        resizable={false}
        minRows={0}
        defaultSorted={
          (customSort && customSort) || (defaultSort && [{ id: "field", desc: true }]) || undefined
        }
        style={{
          border: 0,
          ...this.props.style,
        }}
        filtered={this.getFilteredArray()} // APPLY FILTERS
        defaultFilterMethod={this.defaultFilterMethod}
        column={{
          ...ReactTableDefaults.column,
          headerClassName,
          headerStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: 8,
            border: "1px solid #ebebeb",
            borderLeft: "none",
            borderRight: "none",
            borderTop: "none",
            background: "transparent",
            boxShadow: "none",
            fontSize: "12px",
            fontFamily: "Poppins",
            outline: "none",
          },
          style: { ...defaultColStyle, ...columnStyles },
        }}
        ThComponent={createThComponent("normal")}
        getTheadProps={(state: FinalState, rowInfo: undefined) => {
          return {
            style: {
              boxShadow: "none",
            },
          };
        }}
        getTheadThProps={(
          state: FinalState,
          rowInfo: undefined,
          column: Column | undefined,
          instance: any
        ) => ({
          ...ReactTableDefaults.getTheadThProps,
          column: column && column.id,
          onClick: (e: React.SyntheticEvent<HTMLElement>, handleOriginal: () => any) => {
            this.setState(
              (prevState: TableState) => ({
                columns: prevState.columns.map((c) => {
                  if (c.id === (column && column.id)) {
                    c.desc = !c.desc;
                  } else {
                    c.desc = true;
                  }
                  return c;
                }),
              }),
              () => {
                if (handleOriginal) {
                  handleOriginal();
                }
              }
            );
          },
        })}
        noDataText={emptyState || "No data found"}
        getNoDataProps={() => ({
          style: {
            position: "relative",
            transform: "none",
            width: 150,
            height: 150,
            marginTop: 30,
          },
        })}
        NoDataComponent={({ children, style }: any) => {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "16em",
              }}
            >
              {loading ? (
                <div
                  style={{
                    zIndex: 10000,
                    minHeight: "16em",
                  }}
                >
                  <Loading />
                </div>
              ) : (
                <NoDataAvailable />
              )}
            </div>
          );
        }}
        getTrProps={(state:any, rowInfo:any) => {
          const viewIndex = rowInfo?.viewIndex;

          return {
            style: {
              cursor: onRowClick ? "pointer" : "inherit",

              // FIXED — stable row striping with sorting
              background:
                viewIndex % 2 === 0
                  ? "#FFFFFF"        // even
                  : "#FDF19EC2",     // odd
            },

            onClick: () =>
              onRowClick && rowInfo && onRowClick(rowInfo.row._original),

            className: onRowClick ? "table-row-clickable" : "",
          };
        }}
        getTdProps={() => {
          return { style: { overflow: "visible" } };
        }}
        getTbodyProps={() => {
          return {
            style: this.props.isScrollable ? { overflowY: "scroll" } : { overflow: "visible" },
          };
        }}
        getTableProps={() => {
          return {
            style: this.props.isScrollable ? { overflowY: "scroll" } : { overflow: "visible" },
          };
        }}
      />
    );
  }

  public renderMobileEmptyState() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          style={{ marginTop: 20 }}
          src={emptyTableStateSvg}
          data-qa="empty-state-image"
          alt=""
        />
        <Body
          type="2"
          style={{ marginTop: 12, paddingBottom: 20, color: "#8d8d92" }}
          data-qa="empty-state-text"
        >
          {this.props.emptyState}
        </Body>
      </div>
    );
  }
}
